import type {
  ImportPlan,
  ImportProfileSummary,
  ImportShape,
  ImportSheetSummary,
  PlanColumn,
} from '@ni/shared';
import { unsupportedColumns } from '@ni/shared';
import { IMPORT_SHAPE_MAIL_TO } from '../../lib/copy';
import { sheetFor } from './plan-glance';

/**
 * **The escape hatch** (owner's ruling, 2026-09-07) — one press that turns
 * "nothing here can hold this yet" into a message somebody can act on.
 *
 * The Plan tab can now say that a column holds something real and this build has
 * no shape for it. That is only worth saying if the fact reaches the person who
 * would build the shape, and the office is not going to write that email: they
 * would have to explain a spreadsheet in prose to somebody who has never seen
 * it. So the button composes it — the file's shape, the columns in question, and
 * what the planner made of each — and hands it to their own mail client, opened
 * and unsent. **Nothing is sent by this code.** The office reads the body, edits
 * it if they like, and presses send themselves, which is the only version of
 * this that is theirs to decide.
 *
 * ## What is in it, and what is deliberately not
 *
 * In: the org's name, the line they wrote about the file, the file's name, kind
 * and shape, each unsupported column's header, inferred type, up to three sample
 * values and the planner's sentence about it, and the sheet's own summary — its
 * headers, how many rows stand for a person, and the group titles with counts.
 *
 * Not in: **any row of the file**. Not one. The three samples per column are the
 * three the Plan tab already prints on screen beside that column, and no other
 * cell of the file is read here at all — a body assembled from rows would be a
 * copy of the membership list travelling by email because a column would not map.
 *
 * And not the samples of a column that reads as an email address, a phone number
 * or an identifier ({@link contactShaped}). Three sample values are a fine way
 * to recognise a column of dates or party sizes and a bad way to handle three
 * people's phone numbers. The column, its header and its meaning still go; only
 * the values are dropped, and the body says they were.
 *
 * The profile's own `inferred_type` cannot answer that question — it is
 * `string | number | date | boolean | empty | mixed` and has never had a word
 * for "email" — so the check is on the header and on the shape of the samples
 * themselves, and it is deliberately eager: a column wrongly held back loses
 * three sample values from an email, and a column wrongly let through puts three
 * people's phone numbers in somebody's outbox.
 */

/** The address the shape goes to. Re-exported so the button and its spec agree. */
export { IMPORT_SHAPE_MAIL_TO };

/**
 * **The ceiling on the whole URL**, under the ~8 kB that the shortest-fingered
 * mail clients and browsers still truncate a `mailto:` at. The body is trimmed
 * to fit rather than sent broken — see {@link fit}, which drops the sample lines
 * first, because they are the part of the message that repeats.
 */
export const SHAPE_MAIL_MAX_BYTES = 6_000;

export interface ShapeMailInput {
  /** The org's own name, as the office would say it. */
  readonly orgName: string;
  /** The line the office wrote when they uploaded the file. */
  readonly description: string;
  readonly filename: string;
  /** `csv | xlsx | pdf` — what the bytes arrived in. */
  readonly container: string;
  /** What the parser made of it: a grid or a document. */
  readonly shape: ImportShape;
  readonly plan: ImportPlan;
  readonly profile?: ImportProfileSummary;
}

/** The subject line: the one thing that has to be scannable in an inbox. */
export const shapeMailSubject = (filename: string): string => `Import shape: ${filename}`;

/**
 * The whole `mailto:` URL, ready for an `href`. Empty string when there is
 * nothing to say — the button is not rendered without unsupported columns, and a
 * link that opened a blank mail would be worse than no link.
 */
export function shapeMailto(input: ShapeMailInput): string {
  const columns = unsupportedColumns(input.plan);
  if (columns.length === 0) return '';
  const subject = encodeURIComponent(shapeMailSubject(input.filename));
  const body = encodeURIComponent(shapeMailBody(input, columns));
  return `mailto:${IMPORT_SHAPE_MAIL_TO}?subject=${subject}&body=${body}`;
}

/**
 * **The body, in the order somebody reading it would want it**: who and what
 * file, then the columns that could not be mapped, then the sheet they sit on.
 *
 * Plain text with no markup of any kind. It lands in a mail client the office
 * chose, and every wrapper that renders it — a plain composer, a rich one, a
 * phone — renders lines and blank lines the same way and renders nothing else
 * reliably.
 */
export function shapeMailBody(
  input: ShapeMailInput,
  columns = unsupportedColumns(input.plan),
): string {
  const sheet = sheetFor(input.profile, input.plan.source.sheet);
  const head = [
    `Org: ${input.orgName}`,
    `File: ${input.filename} (${input.container}, read as a ${input.shape})`,
    `Described as: ${input.description || '(nothing said)'}`,
  ];

  /* The columns first and with their own numbering, because they are what the
     mail is about; everything under them is context for reading them. */
  const blocks = columns.map((column, at) => columnBlock(column, at + 1, sheet));

  return fit(head, blocks, sheetBlock(sheet));
}

/* ————————————————————————— one column ————————————————————————— */

/**
 * One unsupported column, as three or four lines: what it is called and where
 * it sits, what the planner made of it, where it would belong if there were
 * somewhere to put it, and — unless it is contact-shaped — three of its values.
 *
 * The sample line is **last within the block** and is the one thing {@link fit}
 * drops, which is why it is written as its own line rather than folded onto the
 * header line.
 */
function columnBlock(
  column: PlanColumn,
  at: number,
  sheet: ImportSheetSummary | undefined,
): { readonly lines: string[]; readonly samples: string | null } {
  const target = column.target;
  const meaning = target.kind === 'unsupported' ? target.meaning : '';
  const hint = target.kind === 'unsupported' ? target.shape_hint : undefined;
  const found = sheet?.columns.find((one) => one.index === column.column.index);
  const header = column.column.header || `column ${column.column.index}`;

  const lines = [
    `${at}. "${header}" (column ${column.column.index}, ${found?.inferred_type ?? 'unknown'})`,
    `   The assistant: ${meaning}`,
  ];
  if (hint) lines.push(`   Would be: ${hint}`);

  const samples = found?.samples ?? [];
  if (samples.length === 0) return { lines, samples: null };
  return {
    lines,
    samples: contactShaped(header, samples)
      ? '   Samples: withheld, since this column reads as contact details'
      : `   Samples: ${samples.slice(0, 3).join(' | ')}`,
  };
}

/**
 * **Whether a column's values are somebody's contact details or their id**, and
 * therefore whether its samples travel.
 *
 * A header test and a value test, either of which is enough. Neither is clever
 * and neither needs to be: the cost of a false positive is three sample values
 * missing from a mail, and the cost of a false negative is three real people's
 * phone numbers in it.
 */
export function contactShaped(header: string, samples: readonly string[]): boolean {
  const name = header.toLowerCase();
  if (
    /\b(e-?mail|phone|mobile|cell|tel|fax|ssn|passport|licen[cs]e|account)\b/.test(name) ||
    /\b(id|ids|uuid|guid|number|no\.?)\b/.test(name)
  ) {
    return true;
  }
  return samples.some((value) => {
    const text = value.trim();
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text)) return true;
    /* **A date is not an identifier**, and it is mostly digits: `2026-04-11` is
       eight of them in ten characters, which the run test below reads as a
       member number. Dates are the commonest thing on a sheet nothing here can
       hold yet — a reservation is a person and a date — so they are named
       before the run test rather than lost to it. */
    if (/^\d{4}-\d{1,2}(-\d{1,2})?$/.test(text) || /^\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}$/.test(text)) {
      return false;
    }
    const digits = text.replace(/\D/g, '');
    /* A phone number, or a bare run of digits long enough to be an id: a year
       and a party size are both shorter than this and travel. */
    return digits.length >= 7 && digits.length / Math.max(text.length, 1) > 0.5;
  });
}

/* ————————————————————————— the sheet ————————————————————————— */

/** What the columns sit on: its headers, its size and its own classification. */
function sheetBlock(sheet: ImportSheetSummary | undefined): string[] {
  if (!sheet) return [];
  const lines = [
    'The sheet',
    `Name: ${sheet.name || '(unnamed)'}`,
    `Rows standing for a person: ${sheet.data_row_count}`,
    `Headers: ${sheet.columns.map((one) => one.header || `column ${one.index}`).join(' | ')}`,
  ];
  if (sheet.group_titles.length > 0) {
    lines.push(
      `Groups: ${sheet.group_titles
        .map((group) => `${group.title} (${group.data_row_count})`)
        .join(' | ')}`,
    );
  }
  return lines;
}

/* ————————————————————————— the budget ————————————————————————— */

/**
 * **The body, trimmed to {@link SHAPE_MAIL_MAX_BYTES} of encoded URL**, dropping
 * the least load-bearing thing first.
 *
 * The order is deliberate and is the order somebody reading the mail would
 * choose themselves. Samples go first: they are the part that repeats once per
 * column, and a mail with every column's meaning and no sample values is still
 * actionable, while one with three columns' samples and the other nine columns
 * missing entirely is not. Then whole column blocks from the end, each replaced
 * by one line saying how many were cut. The head lines and the sheet block are
 * never dropped — a mail that does not say which file it is about is not a
 * shorter mail, it is a useless one.
 *
 * The budget is measured on the **encoded** body, because that is what actually
 * travels: a body of accented names and pipe characters is half again as long
 * once it is percent-encoded, and a byte count taken before that would let a
 * URL through at nearly twice the limit.
 */
function fit(
  head: readonly string[],
  blocks: ReadonlyArray<{ readonly lines: string[]; readonly samples: string | null }>,
  sheet: readonly string[],
): string {
  const assemble = (withSamples: boolean, take: number): string => {
    const kept = blocks.slice(0, take);
    const shown = kept.flatMap((block) =>
      withSamples && block.samples ? [...block.lines, block.samples] : block.lines,
    );
    const cut = blocks.length - kept.length;
    return [
      ...head,
      '',
      `Not importable yet (${blocks.length}):`,
      ...shown,
      ...(cut > 0 ? [`   … and ${cut} more, left out to keep this email short`] : []),
      ...(sheet.length > 0 ? ['', ...sheet] : []),
    ].join('\n');
  };

  const fits = (body: string): boolean => encodeURIComponent(body).length <= SHAPE_MAIL_MAX_BYTES;

  const whole = assemble(true, blocks.length);
  if (fits(whole)) return whole;

  const noSamples = assemble(false, blocks.length);
  if (fits(noSamples)) return noSamples;

  /* Then columns from the end, keeping at least one: a mail naming no column at
     all has nothing in it that the subject line did not already say. */
  for (let take = blocks.length - 1; take >= 1; take -= 1) {
    const shorter = assemble(false, take);
    if (fits(shorter)) return shorter;
  }
  return assemble(false, 1);
}
